import React, { useMemo, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { useDebounce } from "react-use";

import qs from "query-string";

import dayjs from "dayjs";
import Select from "react-select";

import { QUERY_TWILIO_USAGE_OVERVIEW } from "../../config/graphql/query";

import Table from "../../components/Table";
import Pagination from "../../components/Pagination";

import { reactSelectCustomStyles } from "../Employees/Employee/Information";

/**
 * Twilio costs — every client that spent anything in one month, one row each.
 *
 * The per-client report at `/twilio-usage` answers "where did this client's money go".
 * This answers "who costs the most", which until now meant opening clients one by one with
 * no way to tell which of them even has Twilio.
 *
 * Every amount is what Twilio charges *us*. ADMIN-only route over an ADMIN-only query —
 * it must never reach a client.
 */

interface ITwilioUsageCost {
  amount?: number | null;
  currency?: string | null;
}

interface ITwilioCredits {
  calls: number;
  minutes: number;
  segments: number;
  total: number;
}

interface ITwilioCostsRow {
  client: { id: string; title: string };
  calls: {
    records: number;
    startedMinutes: number;
    twilioCost?: ITwilioUsageCost | null;
  };
  sms: {
    records: number;
    segments: number;
    twilioCost?: ITwilioUsageCost | null;
  };
  subaccount: boolean;
  callerId?: string | null;
  numberCost?: ITwilioUsageCost | null;
  totalCost?: ITwilioUsageCost | null;
  credits: ITwilioCredits;
}

interface ITwilioCostsTotals {
  calls?: ITwilioUsageCost | null;
  sms?: ITwilioUsageCost | null;
  numbers?: ITwilioUsageCost | null;
  total?: ITwilioUsageCost | null;
  credits: ITwilioCredits;
}

interface IOverviewQuery {
  twilioUsageOverview: {
    period: string;
    count: number;
    rows: ITwilioCostsRow[];
    totals: ITwilioCostsTotals;
  };
}

const PERIOD_COUNT = 13;

const DEFAULT_LIMIT = 10;

const LIMIT_OPTIONS = [10, 25, 50, 0];

type SortColumn = "client" | "calls" | "sms" | "numbers" | "cost" | "credits";

const SORT_COLUMNS: SortColumn[] = [
  "client",
  "calls",
  "sms",
  "numbers",
  "cost",
  "credits",
];

const DEFAULT_SORT: SortColumn = "cost";

const DEFAULT_ORDER: Record<SortColumn, "asc" | "desc"> = {
  client: "asc",
  calls: "desc",
  sms: "desc",
  numbers: "desc",
  cost: "desc",
  credits: "desc",
};

interface ISearchQuery {
  page?: number;
  search?: string;
  sort?: string;
  order?: string;
  limit?: number;
}

/** Digits of the same width, so the amounts line up down the column. */
const NUMERIC: React.CSSProperties = { fontVariantNumeric: "tabular-nums" };

/**
 * Split rather than formatted into one string, so the amount can be given weight while
 * the currency code stays quiet beside it — repeated down every row, `USD` is the least
 * informative thing on the screen.
 *
 * `null` means there is no amount to show, and the three reasons are deliberately not
 * distinguished here: not priced yet, more than one currency in the month, or a number
 * rent Twilio could not be asked for. All render `—`, never `0.00` — "we do not know" and
 * "it was free" are different answers.
 */
const costParts = (cost?: ITwilioUsageCost | null) => {
  if (cost?.amount === undefined || cost?.amount === null) {
    return null;
  }

  return {
    // Twilio reports what it deducted, so the sign is dropped and the magnitude shown.
    value: Math.abs(cost.amount).toFixed(2),
    currency: cost.currency || "",
  };
};

/**
 * The money leads and the volume that produced it sits underneath. This is a cost table:
 * the amount is the headline, the count is the explanation of it.
 */
const Money = React.memo(
  (props: {
    cost?: ITwilioUsageCost | null;
    note?: React.ReactNode;
    strong?: boolean;
  }) => {
    const parts = costParts(props.cost);

    return (
      <>
        <div
          style={{
            ...NUMERIC,
            // Semibold rather than `font-weight-bold`: Bootstrap 4 has no 600 utility, and
            // full bold on every cell would leave the total nothing to stand out with.
            fontWeight: props.strong ? 700 : 600,
            fontSize: props.strong ? "1.05rem" : undefined,
          }}
        >
          {parts ? (
            <>
              {parts.value}
              {/* Same weight, size and colour as the amount: the currency is part of the
                  figure, and setting it apart made every row look like two things. */}
              {parts.currency ? (
                <span className="ml-1">{parts.currency}</span>
              ) : null}
            </>
          ) : (
            <span className="text-muted font-weight-normal">—</span>
          )}
        </div>
        {props.note ? (
          <div className="small" style={NUMERIC}>
            {props.note}
          </div>
        ) : null}
      </>
    );
  },
);

const ARROW = { asc: "▲", desc: "▼" } as const;

const ARIA_SORT = { asc: "ascending", desc: "descending" } as const;

const SortHeader = React.memo(
  (props: {
    column: SortColumn;
    label: string;
    active: boolean;
    order: "asc" | "desc";
    onSort: (column: SortColumn) => void;
    className?: string;
    title?: string;
  }) => {
    const { column, label, active, order, onSort } = props;

    return (
      <th
        scope="col"
        className={props.className}
        title={props.title}
        aria-sort={active ? ARIA_SORT[order] : "none"}
      >
        <button
          type="button"
          className="btn btn-link p-0 border-0 align-baseline text-reset text-decoration-none font-weight-bold"
          onClick={() => onSort(column)}
        >
          {label}
          <span
            className={`ml-1 ${active ? "" : "text-muted"}`}
            style={{ opacity: active ? 1 : 0.35 }}
            aria-hidden="true"
          >
            {active ? ARROW[order] : ARROW.desc}
          </span>
        </button>
      </th>
    );
  },
);

const TwilioCosts = React.memo(() => {
  const { t } = useTranslation(["twilioCosts", "common"]);

  const periodOptions = useMemo(
    () =>
      Array.from({ length: PERIOD_COUNT }).map((_, index) => {
        const value = dayjs().subtract(index, "month").format("YYYY-MM");

        return { value, label: value };
      }),
    [],
  );

  /**
   * ⚠️ `useLocation`, not `useHistory().location` — the history context value never
   * changes identity, and this page is mounted as a `children` element, so paging would
   * update the URL and re-render nothing.
   */
  const { push } = useHistory();
  const location = useLocation();

  const query = useMemo(
    () => qs.parse(location.search, { parseNumbers: true }) as ISearchQuery,
    [location.search],
  );

  const page = Math.max(Number(query.page) || 1, 1);

  const sort = SORT_COLUMNS.includes(query.sort as SortColumn)
    ? (query.sort as SortColumn)
    : DEFAULT_SORT;

  const order =
    query.order === "asc" || query.order === "desc"
      ? query.order
      : DEFAULT_ORDER[sort];

  /**
   * `limit: 0` is a legitimate value ("all"), so the fallback tests for the parameter
   * being absent rather than for it being falsy.
   */
  const limit = LIMIT_OPTIONS.includes(Number(query.limit))
    ? Number(query.limit)
    : DEFAULT_LIMIT;

  const [period, setPeriod] = useState(periodOptions[0].value);
  const [search, setSearch] = useState(String(query.search || ""));

  const mounted = useRef(false);

  /**
   * Everything but the page number lives in the URL, so paging, sorting and the page size
   * survive each other — and a link to this screen carries what the sender was looking at.
   * Anything that changes what the table contains sends it back to page one.
   */
  const toFirstPage = (next: Partial<ISearchQuery>) => {
    const merged: Record<string, unknown> = {
      ...query,
      search,
      ...next,
      page: 1,
    };

    Object.keys(merged).forEach((key) => {
      if (merged[key] === undefined || merged[key] === "") {
        delete merged[key];
      }
    });

    push({ search: qs.stringify(merged) });
  };

  // Not on mount: the first run would push `page: 1` over a link opened straight to
  // page three.
  useDebounce(
    () => {
      if (!mounted.current) {
        mounted.current = true;

        return;
      }

      toFirstPage({ search });
    },
    500,
    [search],
  );

  /** The same column flips direction; a different one starts in its own default. */
  const onSort = (column: SortColumn) => {
    const flipped = order === "asc" ? "desc" : "asc";

    toFirstPage({
      sort: column,
      order: column === sort ? flipped : DEFAULT_ORDER[column],
    });
  };

  // Sorting and paging are the server's: ordering the ten rows this page happens to hold
  // would reorder the page rather than the month.
  const { data, previousData, loading } = useQuery<IOverviewQuery>(
    QUERY_TWILIO_USAGE_OVERVIEW,
    {
      fetchPolicy: "network-only",
      variables: {
        period,
        // The committed search, not what is being typed — the debounce above is what
        // moves one into the other.
        search: String(query.search || "") || undefined,
        limit,
        skip: limit ? (page - 1) * limit : 0,
        sort,
        order,
      },
    },
  );

  /**
   * ⚠️ The previous answer stays on screen until the next one lands. `network-only` with
   * changed variables leaves `data` undefined while the query is in flight, so the table
   * emptied on every sort, page and month — the page collapsed to nothing and the browser
   * scrolled back up, then everything dropped into place again. `previousData` is the
   * whole fix: the rows, the pager and the totals hold still and are replaced in one go.
   *
   * Nothing is announced while that happens: the ask was no "loading" text, not an empty
   * screen.
   */
  const overview =
    data?.twilioUsageOverview || previousData?.twilioUsageOverview;

  const rows = overview?.rows || [];
  const count = overview?.count || 0;
  const totals = overview?.totals;

  /** `t()` is typed as possibly null; one helper guarantees the string and the fallback. */
  const label = (
    key: string,
    fallback: string,
    values?: Record<string, unknown>,
  ): string => {
    const translated = t(key, { defaultValue: fallback, ...values });

    return typeof translated === "string" ? translated : fallback;
  };

  const limitOptions = useMemo(
    () =>
      LIMIT_OPTIONS.map((value) => ({
        value,
        label: value ? String(value) : label("twilioCosts:rows.all", "All"),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t],
  );

  /**
   * "8 calls (5 min)" — the count names its own unit, because a bare number above a row of
   * amounts reads as more money. Brackets so the two cannot run together when copied out,
   * and dropped at zero where they would only repeat it.
   *
   * One colour throughout: the bracket is the same fact measured differently, not an aside.
   */
  const volume = (records: number, counted: string, quantity: string) => (
    <>
      {counted}
      {records ? <span className="ml-1">({quantity})</span> : null}
    </>
  );

  /** `1 234` — grouped, because credits run into the thousands across all clients. */
  const number = (value: number) => value.toLocaleString();

  /**
   * One part of the grand total: "Calls 128.40 USD". Renders at the same size with or
   * without a value, so the line it sits on is one line either way.
   */
  const totalPart = (name: string, cost?: ITwilioUsageCost | null) => {
    const parts = costParts(cost);

    return (
      <span className="text-nowrap">
        {name}{" "}
        <span className="font-weight-bold">
          {parts ? `${parts.value} ${parts.currency}`.trim() : "—"}
        </span>
      </span>
    );
  };

  const grandTotal = costParts(totals?.total);

  const creditRule = label(
    "twilioCosts:credits.rule",
    "Credits: 1 for setting up a call + 1 for every started minute + 1 per SMS segment. A call nobody answered counts as none.",
  );

  return (
    <div className="container-fluid">
      <nav aria-label="breadcrumb">
        <ol className="breadcrumb my-3">
          <li className="breadcrumb-item active" aria-current="page">
            {t("twilioCosts:nav.title")}
          </li>
        </ol>
      </nav>

      <div className="d-flex flex-wrap align-items-end mb-4">
        <div className="mr-sm-4 mb-2">
          <label htmlFor="period" className="small text-muted mb-1 d-block">
            {t("twilioCosts:filter.period")}
          </label>
          <div style={{ width: "12rem" }}>
            <Select
              inputId="period"
              options={periodOptions}
              value={periodOptions.find((option) => option.value === period)}
              onChange={(option) => {
                setPeriod(option?.value || period);
                toFirstPage({});
              }}
              styles={reactSelectCustomStyles(false)}
            />
          </div>
        </div>

        <div className="mr-sm-4 mb-2">
          <label htmlFor="search" className="small text-muted mb-1 d-block">
            {t("twilioCosts:th.client")}
          </label>
          <div style={{ width: "20rem" }}>
            <input
              id="search"
              type="search"
              className="form-control"
              placeholder={label(
                "twilioCosts:input.placeholder.search",
                "Search client",
              )}
              value={search}
              onChange={({ target: { value } }) => setSearch(value)}
            />
          </div>
        </div>

        <div className="mb-2">
          <label htmlFor="limit" className="small text-muted mb-1 d-block">
            {t("twilioCosts:filter.rows")}
          </label>
          <div style={{ width: "7rem" }}>
            <Select
              inputId="limit"
              options={limitOptions}
              value={limitOptions.find((option) => option.value === limit)}
              onChange={(option) =>
                toFirstPage({ limit: option?.value ?? DEFAULT_LIMIT })
              }
              styles={reactSelectCustomStyles(false)}
            />
          </div>
        </div>

        {/* The month's bill, in the corner the eye lands on first. It covers every row the
            search left rather than the page, so paging cannot move it.

            ⚠️ One line, and the same one line whether or not there are numbers in it. It
            started as a heading over three stacked lines, which existed only once the data
            arrived — so every query pulled the table up by two lines and dropped it back
            when the rows landed. Nothing here may depend on `totals` being present. */}
        <div
          // Not `text-nowrap` on the line itself: each part carries its own, so a value
          // never breaks in half, but a narrow window may wrap between parts rather than
          // pushing the page sideways.
          className="ml-auto text-right mb-2 pl-4"
          style={{ ...NUMERIC, lineHeight: "2rem" }}
          title={
            query.search
              ? (t("twilioCosts:totals.filtered") as string)
              : (t("twilioCosts:totals.title") as string)
          }
        >
          <span className="small text-muted mr-3">
            {totalPart(t("twilioCosts:th.calls") as string, totals?.calls)}
            <span className="mx-2">·</span>
            {totalPart(t("twilioCosts:th.messages") as string, totals?.sms)}
            <span className="mx-2">·</span>
            {totalPart(
              t("twilioCosts:th.ownNumber") as string,
              totals?.numbers,
            )}
            <span className="mx-2">·</span>
            <span title={creditRule}>
              {label("twilioCosts:totals.credits", "{{count}} credits", {
                count: totals?.credits.total || 0,
                // The plural comes from `count`; this is what is printed, so it can be
                // the dash that stands in for a figure nobody has yet.
                formattedCount: totals ? number(totals.credits.total) : "—",
              })}
            </span>
          </span>
          <span className="text-muted text-uppercase small mr-2">
            {t("twilioCosts:th.total")}
          </span>
          <span style={{ fontSize: "1.25rem", fontWeight: 700 }}>
            {grandTotal ? (
              <>
                {grandTotal.value}
                {grandTotal.currency ? (
                  <span className="ml-1">{grandTotal.currency}</span>
                ) : null}
              </>
            ) : (
              <span className="text-muted">—</span>
            )}
          </span>
        </div>
      </div>

      <Table>
        <thead>
          <tr>
            <SortHeader
              column="client"
              label={t("twilioCosts:th.client") as string}
              active={sort === "client"}
              order={order}
              onSort={onSort}
            />
            <SortHeader
              column="calls"
              label={t("twilioCosts:th.calls") as string}
              active={sort === "calls"}
              order={order}
              onSort={onSort}
              className="text-right"
            />
            <SortHeader
              column="sms"
              label={t("twilioCosts:th.messages") as string}
              active={sort === "sms"}
              order={order}
              onSort={onSort}
              className="text-right"
            />
            <SortHeader
              column="numbers"
              label={t("twilioCosts:th.ownNumber") as string}
              active={sort === "numbers"}
              order={order}
              onSort={onSort}
              className="text-right"
            />
            {/* Ruled off from the parts it sums, so the eye can fall straight down it. */}
            <SortHeader
              column="cost"
              label={t("twilioCosts:th.total") as string}
              active={sort === "cost"}
              order={order}
              onSort={onSort}
              className="text-right border-left"
            />
            {/* Last, and ruled off too: it is the only column that is not money. */}
            <SortHeader
              column="credits"
              label={t("twilioCosts:th.credits") as string}
              active={sort === "credits"}
              order={order}
              onSort={onSort}
              className="text-right border-left"
              title={creditRule}
            />
          </tr>
        </thead>
        <tbody>
          {!loading && !rows.length ? (
            <tr>
              <td colSpan={6} className="text-muted">
                {query.search
                  ? t("twilioCosts:emptyFiltered")
                  : t("twilioCosts:empty")}
              </td>
            </tr>
          ) : null}
          {rows.map((row) => (
            <tr key={row.client.id}>
              <td className="align-middle">{row.client.title}</td>
              <td className="text-right text-nowrap">
                <Money
                  cost={row.calls.twilioCost}
                  note={volume(
                    row.calls.records,
                    label("twilioCosts:calls.count", "{{count}} calls", {
                      count: row.calls.records,
                    }),
                    label("twilioCosts:calls.minutes", "{{count}} min", {
                      count: row.calls.startedMinutes,
                    }),
                  )}
                />
              </td>
              <td className="text-right text-nowrap">
                <Money
                  cost={row.sms.twilioCost}
                  note={volume(
                    row.sms.records,
                    label("twilioCosts:sms.count", "{{count}} sms", {
                      count: row.sms.records,
                    }),
                    label("twilioCosts:sms.segments", "{{count}} segments", {
                      count: row.sms.segments,
                    }),
                  )}
                />
              </td>
              {/* Only a subaccount rents a number of its own. On the main account one set
                  of numbers serves everyone, so saying so beats an empty cell that reads
                  as a rendering fault — and beats `—`, which here means "could not ask". */}
              <td className="text-right text-nowrap">
                {row.subaccount ? (
                  <Money cost={row.numberCost} note={row.callerId} />
                ) : (
                  <span className="small text-muted">
                    {label("twilioCosts:mainAccount", "main account")}
                  </span>
                )}
              </td>
              <td className="text-right text-nowrap align-middle border-left">
                <Money strong cost={row.totalCost} />
              </td>
              {/* Not money, so it is not run through `Money`: no currency, no `—`. A
                  client that spent nothing has zero credits, which is a real answer. */}
              <td className="text-right text-nowrap border-left">
                <div
                  style={{ ...NUMERIC, fontWeight: 700, fontSize: "1.05rem" }}
                >
                  {number(row.credits.total)}
                </div>
                {row.credits.total ? (
                  <div className="small" style={NUMERIC} title={creditRule}>
                    {label(
                      "twilioCosts:credits.breakdown",
                      "{{calls}} + {{minutes}} + {{segments}}",
                      {
                        calls: row.credits.calls,
                        minutes: row.credits.minutes,
                        segments: row.credits.segments,
                      },
                    )}
                  </div>
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      {/* The rule spelled out once, under the column it explains — a tooltip alone would
          leave a number nobody can check. */}
      <p className="small text-muted">{creditRule}</p>

      {/* No pager on the "all" page size — `limit > 0`, not `limit`, or the falsy 0 would
          print itself into the page. */}
      {limit > 0 && count > limit ? (
        <Pagination documentsCount={count} limit={limit} />
      ) : null}
    </div>
  );
});

export default TwilioCosts;
